import React, { useEffect, useState } from "react";

import { i18n } from "../../translate/i18n";
import ModalImageChatbot from "../ModalImageChatbot";
import useWhatsApps from "../../hooks/useWhatsApps";
import api from "../../services/api";

function isHubOfficialConnection(whatsapp) {
	const channel = String(whatsapp?.channel || "").toLowerCase();
	return (
		channel === "whatsapp_business_account" ||
		whatsapp?.hub === true ||
		whatsapp?.hub === 1
	);
}

function isApprovedTemplate(template) {
	if (!template?.status) return true;
	return String(template.status).toUpperCase() === "APPROVED";
}

function templateDisplayName(template) {
	return (
		template?.name ||
		template?.elementName ||
		template?.title ||
		String(template?.id || "—")
	);
}

function countPlaceholders(text) {
	if (!text) return 0;
	return (String(text).match(/\{\{\d+\}\}/g) || []).length;
}

export function listTemplateParamSlots(components = []) {
	const slots = [];
	for (const component of components || []) {
		const type = String(component?.type || "").toUpperCase();
		if (type === "HEADER") {
			const format = String(component?.format || "TEXT").toUpperCase();
			if (format === "TEXT" || format === "") {
				const n = countPlaceholders(component.text);
				for (let i = 0; i < n; i += 1) {
					slots.push({
						kind: "header",
						label: `Cabeçalho {{${i + 1}}}`,
					});
				}
			}
			continue;
		}
		if (type === "BODY") {
			const n = countPlaceholders(component.text);
			for (let i = 0; i < n; i += 1) {
				slots.push({
					kind: "body",
					label: `Corpo {{${i + 1}}}`,
				});
			}
			continue;
		}
		if (type === "BUTTONS" && Array.isArray(component.buttons)) {
			for (const btn of component.buttons) {
				if (String(btn?.type || "").toUpperCase() !== "URL") continue;
				const n = countPlaceholders(btn.url || "");
				const btnLabel = btn.text || "Botão";
				for (let i = 0; i < n; i += 1) {
					slots.push({
						kind: "button",
						label: `Botão (${btnLabel}) {{${i + 1}}}`,
					});
				}
			}
		}
	}
	return slots;
}

export function countTemplateParamsFromComponents(components = []) {
	return listTemplateParamSlots(components).length;
}

export function getTemplateHeaderFormat(template) {
	const header = template?.components?.find(
		(c) => String(c?.type || "").toUpperCase() === "HEADER"
	);
	return String(header?.format || "").toUpperCase();
}

export function templateNeedsMedia(format) {
	return ["IMAGE", "VIDEO", "DOCUMENT"].includes(String(format || "").toUpperCase());
}

export function TemplatesTable({
	whatsappId,
	scheduleData,
	setScheduleData,
	setTemplateParamsLength,
	onTemplateResolved,
}) {
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(false);
	const { whatsApps, loading: loadingWhatsApps } = useWhatsApps();

	useEffect(() => {
		if (!whatsappId || loadingWhatsApps) {
			return;
		}

		async function getTemplates() {
			try {
				setLoading(true);

				const whatsapp = whatsApps.find(
					(whatsApp) => Number(whatsApp.id) === Number(whatsappId)
				);

				const useHub =
					isHubOfficialConnection(whatsapp) ||
					!whatsapp?.wabaAccessToken ||
					!whatsapp?.wabaAccountId;

				if (useHub) {
					const { data } = await api.get(`/hub-tamplate?whatsappId=${whatsappId}`);
					const list = Array.isArray(data?.data)
						? data.data
						: Array.isArray(data)
							? data
							: [];
					setTemplates(list.filter(isApprovedTemplate));
					return;
				}

				const { wabaAccessToken, wabaAccountId } = whatsapp;

				const response = await fetch(
					`https://graph.facebook.com/v15.0/${wabaAccountId}/message_templates`,
					{
						method: "GET",
						headers: {
							Authorization: `Bearer ${wabaAccessToken}`,
							"Content-Type": "application/json",
						},
					}
				);
				const data = await response.json();

				const filteredTemplates = (Array.isArray(data?.data) ? data.data : []).filter(
					(template) =>
						String(template?.name || "").includes("notificame") &&
						isApprovedTemplate(template)
				);

				setTemplates(filteredTemplates);
			} catch (error) {
				setTemplates([]);
			} finally {
				setLoading(false);
			}
		}
		getTemplates();
	}, [whatsappId, whatsApps, loadingWhatsApps]);

	const handleTemplateSelected = (template) => {
		const templateId = template.id;
		const total = countTemplateParamsFromComponents(template.components || []);

		setTemplateParamsLength(total);
		setScheduleData({
			...scheduleData,
			templateId,
			template,
		});
	};

	useEffect(() => {
		if (!scheduleData?.templateId) {
			return;
		}

		const found = templates.find(
			(tmpt) => String(tmpt.id) === String(scheduleData.templateId)
		);
		const total = countTemplateParamsFromComponents(found?.components || []);

		setTemplateParamsLength(total);
		if (found && typeof onTemplateResolved === "function") {
			onTemplateResolved(found);
		}
	}, [scheduleData?.templateId, templates, setTemplateParamsLength, onTemplateResolved]);

	const renderHeaderContent = (template) => {
		const headerComponent = template.components?.find((component) => component.type === "HEADER")
			|| template.components?.[0];

		if (!headerComponent) return "—";

		if (headerComponent.format === "TEXT" || !headerComponent.format) {
			return headerComponent.text || "—";
		}

		if (
			headerComponent.format === "IMAGE" &&
			headerComponent.example?.header_handle?.[0]
		) {
			return (
				<ModalImageChatbot
					imageUrl={headerComponent.example.header_handle[0]}
					style={{ width: "10px" }}
				/>
			);
		}

		return headerComponent.format;
	};

	const approvedTemplates = templates.filter(isApprovedTemplate);

	return (
		<div className="overflow-hidden rounded-lg border border-default-200 dark:border-default-700">
			<div className="overflow-x-auto">
				<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
					<thead className="bg-default-150 dark:bg-default-800/60">
						<tr className="text-xs font-medium uppercase tracking-wide text-default-600 dark:text-default-300">
							<th className="px-3 py-2.5 text-start">Nome</th>
							<th className="px-3 py-2.5 text-start">{i18n.t("templates.table.type")}</th>
							<th className="px-3 py-2.5 text-start">{i18n.t("templates.table.header")}</th>
							<th className="px-3 py-2.5 text-start">{i18n.t("templates.table.body")}</th>
							<th className="px-3 py-2.5 text-start">{i18n.t("templates.table.footer")}</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-default-200 dark:divide-default-700">
						{loading && (
							<tr>
								<td colSpan={5} className="px-3 py-8 text-center text-sm text-default-500">
									<div className="inline-flex items-center gap-2">
										<div className="app-modal-spinner !size-4" />
										<span>Carregando templates...</span>
									</div>
								</td>
							</tr>
						)}
						{!loading && approvedTemplates.length === 0 && (
							<tr>
								<td colSpan={5} className="px-3 py-8 text-center text-sm text-default-500">
									Nenhum template encontrado.
								</td>
							</tr>
						)}
						{!loading &&
							approvedTemplates.map((template) => (
								<tr
									key={template.id}
									className={`cursor-pointer text-sm text-default-700 transition-colors dark:text-default-200 ${
										scheduleData?.templateId &&
										String(scheduleData.templateId) === String(template.id)
											? "bg-primary/10"
											: "hover:bg-default-100 dark:hover:bg-default-800"
									}`}
									onClick={() => handleTemplateSelected(template)}
								>
									<td className="max-w-[12rem] px-3 py-3 align-top font-medium" title={templateDisplayName(template)}>
										<div className="truncate">{templateDisplayName(template)}</div>
									</td>
									<td className="px-3 py-3 align-top">
										{template.category
											? i18n.t(`templates.table.types.${template.category}`)
											: template.status || "Custom"}
									</td>
									<td className="max-w-xs px-3 py-3 align-top">
										<div className="break-words">{renderHeaderContent(template)}</div>
									</td>
									<td className="max-w-sm px-3 py-3 align-top">
										<div className="break-words">
											{template.components?.find((component) => component.type === "BODY")?.text || "—"}
										</div>
									</td>
									<td className="max-w-xs px-3 py-3 align-top">
										<div className="break-words">
											{template.components?.find((component) => component.type === "FOOTER")?.text || "—"}
										</div>
									</td>
								</tr>
							))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
